<script lang="ts">
  import { onMount } from 'svelte';
  import { getBookStorage } from '../lib/storage';
  import { settings } from '../stores/settings';
  import { environment } from '../stores/environment';
  import { telegram } from '../stores/telegram';
  import NotificationManager from './NotificationManager.svelte';

  let storageUsed = 0;
  let storageTotal = 5 * 1024 * 1024; // 5MB in bytes
  let storagePercentage = 0;

  onMount(async () => {
    // Initialize settings (apply theme and typography)
    settings.initialize();

    // Initialize environment detection (async - loads Telegram SDK if needed)
    await environment.initialize();

    // Load storage info
    await updateStorageInfo();
  });

  async function updateStorageInfo() {
    try {
      const storage = getBookStorage();
      const info = await storage.getStorageInfo();
      storageUsed = info.used;
      storageTotal = info.quota || storageTotal;
      storagePercentage = info.percentage;
    } catch (error) {
      console.error('Failed to load storage info:', error);
    }
  }

  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  }
</script>

<main class="container mx-auto px-4 py-8 max-w-2xl">
  <h1 class="text-3xl font-bold mb-8">Settings</h1>

  <section class="mb-8">
    <h2 class="text-xl font-semibold mb-4">Appearance</h2>
    <div class="space-y-4">
      <div>
        <label for="theme" class="block text-sm font-medium mb-2">Theme</label>
        <select
          id="theme"
          value={$settings.theme}
          on:change={(e) => settings.setTheme(e.currentTarget.value as any)}
          class="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700"
        >
          <option value="auto">Auto (System)</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </div>

      <div>
        <label for="font-size" class="block text-sm font-medium mb-2">
          Font Size: {$settings.fontSize}px
        </label>
        <input
          id="font-size"
          type="range"
          min="12"
          max="24"
          value={$settings.fontSize}
          on:input={(e) => settings.setFontSize(parseInt(e.currentTarget.value))}
          class="w-full"
        />
      </div>

      <div>
        <label for="font-family" class="block text-sm font-medium mb-2">Font Family</label>
        <select
          id="font-family"
          value={$settings.fontFamily}
          on:change={(e) => settings.setFontFamily(e.currentTarget.value as any)}
          class="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700"
        >
          <option value="sans-serif">Sans Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
        </select>
      </div>

      <div>
        <label for="line-height" class="block text-sm font-medium mb-2">
          Line Height: {$settings.lineHeight.toFixed(1)}
        </label>
        <input
          id="line-height"
          type="range"
          min="1.2"
          max="2.0"
          step="0.1"
          value={$settings.lineHeight}
          on:input={(e) => settings.setLineHeight(parseFloat(e.currentTarget.value))}
          class="w-full"
        />
      </div>

      <div>
        <label for="text-width" class="block text-sm font-medium mb-2">
          Text Width: {$settings.textWidth}px
        </label>
        <input
          id="text-width"
          type="range"
          min="600"
          max="1200"
          step="50"
          value={$settings.textWidth}
          on:input={(e) => settings.setTextWidth(parseInt(e.currentTarget.value))}
          class="w-full"
        />
      </div>
    </div>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-semibold mb-4">Storage</h2>
    <div class="p-4 bg-gray-100 dark:bg-gray-800 rounded">
      <p class="text-sm mb-2">
        Storage used: <span class="font-semibold"
          >{formatBytes(storageUsed)} / {formatBytes(storageTotal)}</span
        >
      </p>
      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div class="bg-blue-600 h-2 rounded-full" style="width: {storagePercentage}%"></div>
      </div>
    </div>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-semibold mb-4">Notifications</h2>
    <div class="bg-gray-100 dark:bg-gray-800 rounded">
      <NotificationManager />
    </div>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-semibold mb-4">Environment</h2>
    {#if $environment}
      <div class="p-4 bg-gray-100 dark:bg-gray-800 rounded space-y-4">
        <!-- Environment Type -->
        <div class="flex items-center justify-between pb-3 border-b border-gray-300 dark:border-gray-600">
          <span class="text-sm font-medium">Environment:</span>
          <span class="text-sm font-semibold text-blue-600 dark:text-blue-400">
            {$environment.displayName}
          </span>
        </div>

        <!-- Platform Info -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-400">Platform:</span>
            <span class="text-sm font-medium">{$environment.nativePlatform.toUpperCase()}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-400">Is Native:</span>
            <span class="text-sm font-medium">{$environment.isNative ? '✅ Yes' : '❌ No'}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-400">Screen:</span>
            <span class="text-sm font-mono">{$environment.screenWidth}×{$environment.screenHeight} @{$environment.pixelRatio}x</span>
          </div>
        </div>

        <!-- Status Indicators -->
        <div class="grid grid-cols-2 gap-2 pt-3 border-t border-gray-300 dark:border-gray-600">
          <div class="flex items-center space-x-2">
            <span class={`w-2 h-2 rounded-full ${$environment.isMobile ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            <span class="text-sm">Mobile Device</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class={`w-2 h-2 rounded-full ${$environment.isPWA ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            <span class="text-sm">PWA Installed</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class={`w-2 h-2 rounded-full ${$environment.isTelegram ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            <span class="text-sm">Telegram App</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class={`w-2 h-2 rounded-full ${$environment.isCapacitor ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            <span class="text-sm">Capacitor App</span>
          </div>
        </div>

        <!-- Capacitor Details -->
        {#if $environment.capacitor}
          <div class="pt-3 border-t border-gray-300 dark:border-gray-600">
            <details class="text-sm">
              <summary class="cursor-pointer font-medium mb-2">📱 Capacitor Details</summary>
              <div class="mt-2 space-y-2 text-xs">
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Platform:</span>
                  <span class="font-medium">{$environment.capacitor.platform}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Native:</span>
                  <span class="font-medium">{$environment.capacitor.isNativePlatform ? 'Yes' : 'No'}</span>
                </div>
                {#if $environment.capacitor.nativeVersion}
                  <div class="flex justify-between">
                    <span class="text-gray-600 dark:text-gray-400">Version:</span>
                    <span class="font-medium">{$environment.capacitor.nativeVersion}</span>
                  </div>
                {/if}
              </div>
            </details>
          </div>
        {/if}

        <!-- Telegram Details -->
        {#if $environment.telegram}
          <div class="pt-3 border-t border-gray-300 dark:border-gray-600">
            <details class="text-sm">
              <summary class="cursor-pointer font-medium mb-2">✈️ Telegram Details</summary>
              <div class="mt-2 space-y-2 text-xs">
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Platform:</span>
                  <span class="font-medium">{$environment.telegram.platform}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Version:</span>
                  <span class="font-medium">{$environment.telegram.version}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Color Scheme:</span>
                  <span class="font-medium">{$environment.telegram.colorScheme}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Viewport:</span>
                  <span class="font-medium">{$environment.telegram.viewportHeight}px</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Stable Height:</span>
                  <span class="font-medium">{$environment.telegram.viewportStableHeight}px</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Expanded:</span>
                  <span class="font-medium">{$environment.telegram.isExpanded ? 'Yes' : 'No'}</span>
                </div>
                {#if $environment.telegram.safeAreaInsetTop > 0 || $environment.telegram.safeAreaInsetBottom > 0}
                  <div class="flex justify-between">
                    <span class="text-gray-600 dark:text-gray-400">Safe Area:</span>
                    <span class="font-medium">
                      ↑{$environment.telegram.safeAreaInsetTop}px ↓{$environment.telegram.safeAreaInsetBottom}px
                    </span>
                  </div>
                {/if}
              </div>
            </details>
          </div>
        {/if}

        <!-- Technical Details -->
        <div class="pt-3 border-t border-gray-300 dark:border-gray-600">
          <details class="text-sm">
            <summary class="cursor-pointer font-medium mb-2">🔧 Technical Details</summary>
            <div class="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <div>
                <span class="font-medium">Platform:</span> {$environment.platform}
              </div>
              <div>
                <span class="font-medium">Standalone:</span> {$environment.standalone ? 'Yes' : 'No'}
              </div>
              <div class="break-all">
                <span class="font-medium">User Agent:</span><br />
                {$environment.userAgent}
              </div>
            </div>
          </details>
        </div>
      </div>
    {:else}
      <div class="p-4 bg-gray-100 dark:bg-gray-800 rounded">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Detecting environment...
        </p>
      </div>
    {/if}
  </section>

  {#if $environment?.isTelegram && $telegram.isAvailable}
    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Telegram Mini App</h2>
      <div class="p-4 bg-gray-100 dark:bg-gray-800 rounded space-y-3">
        {#if $telegram.user}
          <div class="flex items-center justify-between pb-3 border-b border-gray-300 dark:border-gray-600">
            <span class="text-sm font-medium">User Information</span>
            <span class="text-xs px-2 py-1 bg-blue-500 text-white rounded">Connected</span>
          </div>

          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-sm text-gray-600 dark:text-gray-400">Name:</span>
              <span class="text-sm font-medium">
                {$telegram.user.first_name}
                {#if $telegram.user.last_name}
                  {$telegram.user.last_name}
                {/if}
                {#if $telegram.user.is_premium}
                  <span class="text-yellow-500">⭐</span>
                {/if}
              </span>
            </div>

            {#if $telegram.user.username}
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">Username:</span>
                <span class="text-sm font-medium">@{$telegram.user.username}</span>
              </div>
            {/if}

            <div class="flex justify-between">
              <span class="text-sm text-gray-600 dark:text-gray-400">User ID:</span>
              <span class="text-sm font-mono">{$telegram.user.id}</span>
            </div>

            {#if $telegram.user.language_code}
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">Language:</span>
                <span class="text-sm font-medium">{$telegram.user.language_code.toUpperCase()}</span>
              </div>
            {/if}
          </div>
        {:else}
          <div class="text-sm text-gray-600 dark:text-gray-400">
            No user information available
          </div>
        {/if}

        {#if $telegram.webAppData}
          <div class="pt-3 border-t border-gray-300 dark:border-gray-600">
            <details class="text-sm">
              <summary class="cursor-pointer font-medium mb-2">WebApp Details</summary>
              <div class="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
                <div>
                  <span class="font-medium">Platform:</span> {$telegram.webAppData.platform}
                </div>
                <div>
                  <span class="font-medium">Version:</span> {$telegram.webAppData.version}
                </div>
                <div>
                  <span class="font-medium">Color Scheme:</span> {$telegram.webAppData.colorScheme}
                </div>
                <div>
                  <span class="font-medium">Viewport Height:</span> {$telegram.webAppData.viewportHeight}px
                </div>
                <div>
                  <span class="font-medium">Background:</span>
                  <span class="inline-block ml-2 px-2 py-0.5 rounded" style="background-color: {$telegram.webAppData.backgroundColor}; color: {$telegram.webAppData.themeParams.text_color || '#ffffff'};">
                    {$telegram.webAppData.backgroundColor}
                  </span>
                </div>
              </div>
            </details>
          </div>
        {/if}
      </div>
    </section>
  {/if}

  <section>
    <h2 class="text-xl font-semibold mb-4">About</h2>
    <p class="text-sm text-gray-600 dark:text-gray-400">
      Legere v0.1.0<br />
      A markdown book reader PWA<br />
      <a href="https://github.com/mguzelevich/cc-legere" class="text-blue-600 dark:text-blue-400 hover:underline">
        View on GitHub
      </a>
    </p>
  </section>
</main>
